import { useEffect, useState } from "react"
import { Check, ChevronDown, Minus, X as XIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Orb, type OrbKind } from "../Orb/Orb"
import type { ToolCardMessage, ToolCardStatus } from "../types/chat"

export interface ChatToolCardProps {
  message: ToolCardMessage
  /** What the orb says while the card is running. */
  orb?: OrbKind
  stageCycleIntervalMs?: number
  className?: string
}

export function ChatToolCard({
  message,
  orb = "running",
  stageCycleIntervalMs = 1600,
  className,
}: ChatToolCardProps) {
  const [expanded, setExpanded] = useState(false)

  const hasConfig = Array.isArray(message.config) && message.config.length > 0
  const isRunning = message.status === "running"
  const canExpand = hasConfig && !isRunning

  if (isRunning) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 text-caption text-muted-foreground",
          className,
        )}
      >
        <Orb state={orb} size={20} aria-hidden />
        <span className="truncate">
          {message.label} |{" "}
          <StageText
            stage={message.stage}
            stageSequence={message.stageSequence}
            intervalMs={stageCycleIntervalMs}
          />
        </span>
      </div>
    )
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => canExpand && setExpanded((e) => !e)}
        disabled={!canExpand}
        aria-expanded={canExpand ? expanded : undefined}
        className={cn(
          "flex items-center gap-2 text-caption text-muted-foreground",
          canExpand && "cursor-pointer hover:text-foreground",
          !canExpand && "cursor-default",
        )}
      >
        <StatusIcon status={message.status} />
        <span className="truncate">{message.label}</span>
        {message.duration && message.duration !== "\u2014" && (
          <span className="shrink-0">{message.duration}</span>
        )}
        {canExpand && (
          <ChevronDown
            size={12}
            strokeWidth={1.5}
            className={cn(
              "shrink-0 transition-transform duration-150",
              expanded && "rotate-180",
            )}
          />
        )}
      </button>

      {expanded && hasConfig && (
        <div className="pl-5 pt-1.5 pb-1">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-caption">
            {message.config!.map((c) => (
              <div key={c.label} className="contents">
                <div className="text-muted-foreground">{c.label}</div>
                <div className="text-foreground">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface StatusIconProps {
  status: ToolCardStatus
}

function StatusIcon({ status }: StatusIconProps) {
  if (status === "done") {
    return (
      <Check
        size={12}
        strokeWidth={2}
        className="shrink-0 text-muted-foreground"
        aria-label="Done"
      />
    )
  }
  if (status === "error") {
    return (
      <XIcon
        size={12}
        strokeWidth={2}
        className="shrink-0 text-destructive"
        aria-label="Error"
      />
    )
  }
  return (
    <Minus
      size={12}
      strokeWidth={2}
      className="shrink-0 text-muted-foreground"
      aria-label="Canceled"
    />
  )
}

interface StageTextProps {
  stage?: string
  stageSequence?: string[]
  intervalMs: number
}

function StageText({ stage, stageSequence, intervalMs }: StageTextProps) {
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    if (!stageSequence || stageSequence.length <= 1) return
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % stageSequence.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [stageSequence, intervalMs])

  if (stageSequence && stageSequence.length > 0) {
    return <span>{`${stageSequence[idx]}\u2026`}</span>
  }
  if (stage) return <span>{`${stage}\u2026`}</span>
  return <span>{"Running\u2026"}</span>
}
